import React, { useEffect, useState } from "react";
import "../../../tenantComponent/components/tenantDashboard/dashboard.css";

import ContractorHeader from "../../common/header/ContractorHeader";
import ProfileCard from "../../../tenantComponent/common/profileCard/ProfileCard";
import UnitCardItem from "../../../newComponents/unit/UnitCardItem";
import DynamicTabs from "../../../newComponents/tabs/DynamicTabs";
import SimpleSpinner from "../../../components/loader";
import { CheckCircle, CreditCard, Briefcase, Wrench, Settings, Wallet } from "lucide-react";
import { getServiceSubTypeNames, getServiceTypeNames } from "../../../utils/enumHelpers";

import { getContractorDashboardTabs } from "../tabs/contractorDashboardTabs";
import { useContractorData } from "../../hooks/useContractorData";
import { Route, Routes, Navigate, useNavigate } from "react-router";
import apiHelper from "../../../utils/apiHelper";
import { getContractorOrganizationHeaderInfo } from "../../../services/contractorOrganization.service";
import { usePayments } from "../../../tenantComponent/hooks/usePayments";
import GoodbyePage from "../../../components/common/GoodbyePage/GoodbyePage";

const ContractorDashboard: React.FC = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("payments");
  const [selectedFilter, setSelectedFilter] = useState("All Payments");

  // ----- Contractor Info -----
  const contractorInfo = JSON.parse(
    sessionStorage.getItem("contractorInfo") || "{}",
  );
  const contractorId = Number(
    contractorInfo?.id ??
      contractorInfo?.contractorId ??
      sessionStorage.getItem("contractorId") ??
      0,
  );

  // ----- Hooks -----
  const { contractorData, loading: contractorLoading, notFound: contractorNotFound } =
    useContractorData(contractorId);

  const unitData = {
    unitName: "S23",
    propertyDetails: { address: "NY" },
    unitType: "Studio",
    size: 7894,
  };

  const [OrgDetail, setOrgDetail] = useState<any>();
  const [orgDetailLoading, setOrgDetailLoading] = useState(true);

  // ----- Payments Hook -----
  const { filteredRows, loading, fetchData, sentryRef } = usePayments(
    activeTab === "payments",
    selectedFilter,
    contractorId,
    true, // isContractor = true
  );

  // ----- Fetch data when tab changes -----
  useEffect(() => {
    if (activeTab === "payments") {
      fetchData(1);
    }
  }, [activeTab]);

  // ----- Dropdown menu -----
  const menuItems = [
    { label: "All Payments", action: () => setSelectedFilter("All Payments") },
    { label: "Next 30 Days", action: () => setSelectedFilter("Next 30 Days") },
    {
      label: "Next 3 Months",
      action: () => setSelectedFilter("Next 3 Months"),
    },
  ];

  // ----- Tabs config -----
  const tabs = getContractorDashboardTabs(
    activeTab,
    setActiveTab,
    contractorId,
    filteredRows,
    loading,
    fetchData,
    sentryRef,
    selectedFilter,
    menuItems,
  );

  useEffect(() => {
    // Contractors can no longer read the Organization entity directly; fetch the org's basic info
    // via the JWT-scoped contractor propagation endpoint instead of GET /api/Organization/{id}.
    apiHelper(getContractorOrganizationHeaderInfo, { showNotification: false })
      .then((res) => {
        setOrgDetail(res?.data?.success);
      })
      .catch((error) => {
        console.log("Error fetching contractor organization header:", error);
      })
      .finally(() => {
        setOrgDetailLoading(false);
      });
  }, []);

  const email = contractorData?.email;
  const phone = contractorData?.formatted?.PhoneNumber || contractorData?.phoneNumber;
  const status = contractorData?.formatted?.UserStatus;
  const expensesDue = contractorData?.formatted?.ExpensesDue;

  const isInHouse = contractorData?.isInHouse;
  const contractorType = isInHouse !== undefined ? (isInHouse ? "In House" : "Third Party") : undefined;
  const categories = contractorData?.serviceTypesIds ? getServiceTypeNames(contractorData.serviceTypesIds, { humanReadable: true }) : undefined;
  const subCategories = contractorData?.serviceSubTypesIds ? getServiceSubTypeNames(contractorData.serviceSubTypesIds, { humanReadable: true }) : undefined;
  const payeeAccount = contractorData?.payeeAccountNames?.[0];

  const additionalInfo = [
    ...(contractorType ? [{
      label: "Type",
      value: contractorType,
      icon: <Briefcase size={16} />,
    }] : []),
    ...(categories ? [{
      label: "Category",
      value: categories,
      icon: <Wrench size={16} />,
    }] : []),
    ...(subCategories ? [{
      label: "Sub Categories",
      value: subCategories,
      icon: <Settings size={16} />,
    }] : []),
    ...(payeeAccount ? [{
      label: "Payee Account",
      value: payeeAccount,
      icon: <Wallet size={16} />,
    }] : []),
  ];

  // ----- Contractor Goodbye -----
  // Fires when either: (a) no contractorId ever resolved -- a deleted contractor's login still
  // succeeds (see LoginController.BuildJwtToken) but issues no ContractorId claim, or (b) the live
  // fetch confirms via a genuine 404 that the contractor no longer exists (a stale session from
  // before deletion). Never on a transient fetch failure (server down, network error) -- those fall
  // through to the normal render below, unchanged.
  if (!contractorLoading && (!contractorId || contractorId === 0 || contractorNotFound)) {
    return (
      <GoodbyePage
        name={contractorInfo?.firstName}
        message="Thank you for your service. It has been a pleasure working with you."
        subtext="Your account is no longer active."
        onLogout={() => {
          sessionStorage.clear();
          navigate("/login", { replace: true });
        }}
      />
    );
  }

  return (
    <div className="dashboard-container">
      {contractorLoading || orgDetailLoading ? (
        <div className="dashboard-loader-wrapper" style={{ height: "100vh" }}>
          <SimpleSpinner myClassName="Icon50" />
        </div>
      ) : (
        <>
          <ContractorHeader
            OrgDetail={OrgDetail}
          />
          <div className="tenant-dashboard-content">
            {/* -------- Profile & Lease Section -------- */}
            <div className="pofile-lease-section">
              <ProfileCard
                name={contractorData?.fullName}
                email={email}
                phone={phone}
                initials={contractorData?.initials}
                info1={status ? {
                  label: "Status",
                  value: status,
                  icon: <CheckCircle size={16} />,
                } : undefined}
                info2={expensesDue !== undefined ? {
                  label: "Expenses Due",
                  value: `$${expensesDue}`,
                  icon: <CreditCard size={16} />,
                } : undefined}
                additionalInfo={additionalInfo}
                onLogout={() => {
                  sessionStorage.clear();
                  window.location.reload();
                  navigate("/login");
                }}
              />
            </div>

            {/* -------- Main Dashboard Section -------- */}
            <div className="main-dashboard-section">
              <Routes>
                <Route
                  index
                  element={
                    <div className="dashboard-with-tabs">
                      <DynamicTabs tabs={tabs} defaultActiveTab="payments" />
                    </div>
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ContractorDashboard;
